import { css, CSSResult, unsafeCSS } from 'lit';

/**
 * Cell rules shared by the item tables that render an expandable quote-item row
 * (quote items, purchase-order items, fabrication-job items). Each of those tables
 * passes these to its <webmodule-table> via `customStyles`, because the table renders
 * cell content inside its shadow DOM where the page stylesheet cannot reach it.
 *
 * The tables use the same class vocabulary with a per-table prefix - `quote-item-title`
 * vs `order-item-title` and so on - so the prefix is spliced in rather than the classes
 * renamed: the legacy light-DOM tables still rely on those names in dealer_ui.scss.
 *
 * Private, and evaluated once per prefix into the constants below: `customStyles()` feeds
 * a reactive property, so a caller that built one of these per render would re-apply the
 * constructed stylesheet every time. Exporting the results rather than the builder makes
 * that mistake unavailable.
 */
function expandableItemCellStyles(prefix: string): CSSResult {
  const p = unsafeCSS(prefix);
  return css`
    /* Title cell: stack the location link above the description (legacy rendered each
       child at 100% width). Without this the cell's default row layout sits them side
       by side and the short link wraps mid-word ("Locati on"). */
    .wm-table-content .wm-table-cell.${p}title {
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      gap: 0;
      line-height: 1.1;
    }
    .${p}title .quote-item-link,
    .${p}title .quote-item-title,
    .${p}title .quote-item-description {
      max-width: 100%;
    }

    /* Options cell: dimension text on the left, expand caret pinned to the right edge.
       The cell is a flexbox in the table's shadow DOM, so the legacy Bootstrap float-end
       no longer applies; margin-left:auto pushes the caret over. */
    .wm-table-content .wm-table-cell.${p}description .options-dropdown {
      margin-left: auto;
      cursor: pointer;
      color: var(--st-blue);
    }
    /* Options header: the expand/collapse-all chevron. */
    .wm-table-header .wm-table-cell.${p}description webmodule-icon {
      margin-left: 0.5rem;
      cursor: pointer;
    }

    /* Tags column icon spacing. */
    .${p}tags webmodule-icon {
      margin-right: 0.25rem;
    }
  `;
}

/** For the tables whose cells carry the `quote-item-*` classes (quote items, fabrication job items). */
export const quoteItemCellStyles = expandableItemCellStyles('quote-item-');

/** For the purchase-order items table, whose cells carry the `order-item-*` classes. */
export const orderItemCellStyles = expandableItemCellStyles('order-item-');
